import React, { useState, useEffect } from "react";
import { Typography, Tag, Space, Spin, Button, Modal } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import config from "../../../api_config.json";
import { useAppContext } from "../../../context";
import CompanySection from "./CompanySection";
import OrgOfficesMap from "./OrgOfficesMap";

const { Title } = Typography;

const officesQuery = `
query GetOrgOffices($orgId: ID!) {
  companies(
    where: {
      org_pointer: { have: { objectId: { equalTo: $orgId } } }
    }
    order: [enabled_DESC, nome_ASC]
  ) {
    edges {
      node {
        objectId
        nome
        ragione_sociale
        enabled
        nascondi
        partita_iva
        codice_fiscale
        regime_fiscale
        codice_destinatario
        fatturazione_elettronica
        indirizzo_fatturazione
        citta_fatturazione
        cap_fatturazione
        provincia_fatturazione
        nazione
        email_fatturazione
        pec
        iban
        intestatario_banca
        nome_banca
        sito
        alias
        slug
        logo { url }
        logo_completo { url }
        logo_sito { url }
        timbro { url }
        cover_image { url }
        colore_principale
        colore_secondario
        colore_testo
        colore_testo_intestazione
      }
    }
  }
  offices(
    where: {
      company_pointer: {
        have: {
          org_pointer: {
            have: { objectId: { equalTo: $orgId } }
          }
        }
      }
    }
    order: [enabled_DESC, citta_ASC]
  ) {
    edges {
      node {
        objectId
        name
        citta
        frazione
        indirizzo
        cap
        provincia
        telefono1
        telefono2
        cellulare1
        cellulare2
        email
        pec
        enabled
        nascondi
        logo { url }
        logo_completo { url }
        logo_sito { url }
        company_pointer {
          objectId
        }
        users {
          edges {
            node {
              objectId
              first_name
              last_name
              email
              telefono
              profile_picture { url }
            }
          }
        }
      }
    }
  }
  office_coords(
    where: {
      office_pointer: {
        have: {
          org_pointer: {
            have: { objectId: { equalTo: $orgId } }
          }
        }
      }
    }
  ) {
    edges {
      node {
        objectId
        latitude
        longitude
        office_pointer {
          objectId
          company_pointer { objectId }
        }
      }
    }
  }
}
`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const OrgOffices = ({ orgId }) => {
  const [loading, setLoading] = useState(false);
  const [companyGroups, setCompanyGroups] = useState([]);
  const [allCoords, setAllCoords] = useState([]);
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) return;

      try {
        setLoading(true);
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: officesQuery,
            variables: { orgId },
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Build company groups from companies query (includes companies without offices)
        const groupMap = new Map();

        if (response.data?.companies?.edges) {
          response.data.companies.edges.forEach((edge) => {
            const company = edge.node;
            groupMap.set(company.objectId, {
              company,
              offices: [],
              coords: [],
            });
          });
        }

        // Associate offices to their company group
        if (response.data?.offices?.edges) {
          response.data.offices.edges.forEach((edge) => {
            const office = edge.node;
            const companyId = office.company_pointer?.objectId || "no-company";

            if (!groupMap.has(companyId)) {
              groupMap.set(companyId, {
                company: { objectId: companyId, nome: "Senza azienda" },
                offices: [],
                coords: [],
              });
            }

            groupMap.get(companyId).offices.push({
              ...office,
              users: office.users?.edges?.map((ue) => ue.node) || [],
            });
          });
        }

        // Parse coordinates and associate to companies
        const coordsList = [];

        if (response.data?.office_coords?.edges) {
          response.data.office_coords.edges.forEach((edge) => {
            const coord = edge.node;
            const officePointer = coord.office_pointer;
            const lat = parseFloat(coord.latitude);
            const lng = parseFloat(coord.longitude);

            if (isNaN(lat) || isNaN(lng) || !officePointer) return;

            // Find the office data to enrich the coord
            const companyId = officePointer.company_pointer?.objectId || "no-company";
            const group = groupMap.get(companyId);
            const officeData = group?.offices.find(
              (o) => o.objectId === officePointer.objectId
            );

            const coordObj = {
              officeId: officePointer.objectId,
              position: [lat, lng],
              city: officeData?.citta || "",
              address: officeData?.indirizzo || "",
              cap: officeData?.cap || "",
              province: officeData?.provincia || "",
              companyName: group?.company?.nome || "",
              enabled: officeData?.enabled,
            };

            coordsList.push(coordObj);

            if (group) {
              group.coords.push(coordObj);
            }
          });
        }

        setCompanyGroups(Array.from(groupMap.values()));
        setAllCoords(coordsList);
      } catch (error) {
        console.error("Error fetching offices data:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, getTokenSilently, env]);

  const totalOffices = companyGroups.reduce(
    (sum, g) => sum + g.offices.length,
    0
  );
  const activeOffices = companyGroups.reduce(
    (sum, g) => sum + g.offices.filter((o) => o.enabled).length,
    0
  );

  return (
    <div style={{ margin: "16px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>Aziende e Sedi</Title>
        {allCoords.length > 0 && (
          <Button
            icon={<EnvironmentOutlined />}
            onClick={() => setMapModalVisible(true)}
          >
            Mappa Sedi
          </Button>
        )}
      </div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Tag color="blue">{companyGroups.length} aziende totali</Tag>
        <Tag color="green">Sedi attive totali: {activeOffices}</Tag>
        <Tag color="red">Sedi disattive totali: {totalOffices - activeOffices}</Tag>
      </Space>

      <Spin spinning={loading}>
        {companyGroups.map((group) => (
          <CompanySection
            key={group.company.objectId}
            company={group.company}
            offices={group.offices}
            coords={group.coords}
          />
        ))}

        {companyGroups.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
            Questa organizzazione non ha aziende o sedi in Eterno
          </div>
        )}
      </Spin>

      <Modal
        title="Mappa Sedi"
        open={mapModalVisible}
        onCancel={() => setMapModalVisible(false)}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        destroyOnClose
      >
        <OrgOfficesMap coords={allCoords} />
      </Modal>
    </div>
  );
};

export default OrgOffices;
